import { AfterViewInit, Component, ElementRef, NgZone, ViewChild, input, output } from '@angular/core';
import { acceptCompletion, CompletionContext } from "@codemirror/autocomplete";
import { indentWithTab } from "@codemirror/commands";
import { css, cssLanguage } from "@codemirror/lang-css";
import { EditorState, Extension } from '@codemirror/state';
import { oneDark } from '@codemirror/theme-one-dark';
import { keymap, placeholder } from "@codemirror/view";
import { basicSetup, EditorView } from 'codemirror';
import { RouteShareService } from 'src/app/shared/services/route-share.service';

@Component({
  selector: 'app-codemirror-css',
  imports: [],
  standalone: true,
  templateUrl: './codemirror-css.component.html',
  styleUrl: './codemirror-css.component.scss'
})
export class CodemirrorCssComponent implements AfterViewInit {
  public readonly instruction = input.required<string>();
  public readonly placeholder = input<string>('');
  public readonly rows = input<number>(5);
  public readonly ragAdmin = input<boolean>(false);
  public readonly botlyContext = input<string>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  public readonly finish = output<string>();
  @ViewChild('codemirror') protected codemirror!: ElementRef<HTMLDivElement>;
  protected editorView: EditorView | undefined;
  protected editorState: EditorState | undefined;
  private extensions: Extension[] = [];

  constructor(private rss: RouteShareService, private zone: NgZone) { }

  public ngAfterViewInit(): void {
    const editorElement = this.codemirror.nativeElement;
    //Erste Codezeile ist immer 30,39px hoch (Zeile -> 22,3906px + Space -> 8px)
    //Jede weitere Zeile fügt 22,3906px hinzu
    const heightToRows = 8 + this.rows() * 22.3906;
    this.extensions = [
      basicSetup,
      keymap.of([{ key: "Tab", run: acceptCompletion }, indentWithTab]),
      placeholder(this.placeholder()),
      css(),
      cssLanguage.data.of({ autocomplete: this.myCompletions.bind(this) }),
      EditorState.readOnly.of(!this.ragAdmin() && !this.rss.userCanEditBotConfig()),
      oneDark,
      EditorView.theme({
        ".cm-gutters": {
          backgroundColor: "#00A599",
          color: "#fff"
        },
        ".cm-activeLineGutter": {
          backgroundColor: "#99DBD6"
        },
        ".cm-content": {
          minHeight: heightToRows + "px"
        },
        ".cm-gutter": {
          minHeight: heightToRows + "px"
        },
        "&.cm-editor": {
          height: "100%"
        },
        ".cm-scroller": {
          overflow: "auto"
        }
      }),
      EditorView.domEventHandlers({
        focusout: () => { this.saveToolConfig(); },
        keydown: (e: KeyboardEvent) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 's') {
            e.stopPropagation();
            e.preventDefault();
            this.saveToolConfig();
          }
        }
      })
    ];
    const myExt = this.extensions;

    try {
      this.editorState = EditorState.create({
        doc: this.instruction(),
        extensions: myExt
      });
    } catch (e) {
      console.error(e);
    }
    this.editorView = new EditorView({
      state: this.editorState,
      parent: editorElement
    });
  }

  public myCompletions(context: CompletionContext) {
    const word = context.matchBefore(/[\w.]*$/);
    if (word?.from == word?.to && !context.explicit) {
      return null;
    }
    const knownSelectors: string[] = [
      "#chatbot-wrapper",
      "#chat-container",
      "#bot-header",
      "#bot-content",
      "#bot-footer",
      "#messages",
      ".message-row",
      ".bubble",
      ".bubble-addons"
    ];
    return {
      from: word?.from,
      options: [
        ...knownSelectors.map(selector => ({ label: selector, apply: selector.substring(1), info: "🪄" })),
        {
          label: "🪄 CSS Gerüst Einfügen (CHATBOT)",
          apply: `/* CSS GERÜST (CHATBOT) */
#chatbot-wrapper {
  #chat-container {
    #bot-header {
    }
    #bot-content {
      #messages {
        .message-row {
          .bubble {
          }
          .bubble-addons {
          }
        }
      }
    }
    #bot-footer{
    }
  }
}`
        },
        {
          label: "🎨 CSS Farbwerte einfügen",
          apply: `/* CSS Farbwerte */
* {
  --bubble-assistant-bg-color: #E5E5EA;
  --bubble-assistant-text-color: #000000;
  --bubble-user-bg-color: #0B93F6;
  --bubble-user-text-color: white;
  --chatbot-background: #fff;
  --chatbot-theme-color: #00A599;
  --chatbot-avatar-img-width: 40px;
  --chatbot-avatar-img-height: 40px;
  --audiobot-radius: 220px;
  --audiobot-bot-color: rgba(92, 141, 246, 0.2);
  --audiobot-bot-shadow-color: rgba(92, 141, 246, 0.6);
  --audiobot-user-color: rgba(92, 141, 246, 0.3);
  --audiobot-thinking-color-1: #000;
  --audiobot-thinking-color-2: transparent;
  --audiobot-background: #fff;
}
`
        },
        {
          label: "🎨 Header Ausblenden",
          apply: `/* Header Ausblenden */
  #bot-header { display: none;}
  #chat-container{ grid-template-rows: 1fr 100px !important; }
`
        }
      ]
    };
  }

  public saveToolConfig() {
    if (this.editorView) {
      const currentCode = this.editorView.state.doc.toString();
      if (this.instruction() !== currentCode) this.finish.emit(currentCode);
    }
  }

  public isBotlyLoading = false;

  public openBotly(): void {
    if (this.isBotlyLoading) return;
    this.isBotlyLoading = true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (window as any).cssBotlyReady = () => {
      this.zone.run(() => { this.onBotlyReady(); });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (window as any).cssFromBotly = (css: string) => {
      this.zone.run(() => { this.applyBotlyCss(css); });
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (window as any).RAGBOT?.setChatbotUrlOnce('https://ki-toolbox.reflactportal.com/de/bot/6a0f1bb335e0970f97e685ec/chat');
  }

  private onBotlyReady(): void {
    const currentCss = this.editorView?.state.doc.toString() ?? '';
    const ctx = JSON.stringify(this.botlyContext() ?? 'CSS');
    const cssMsg = JSON.stringify('Aktuelles CSS:\n' + currentCss);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (window as any).RAGBOT?.send(`RAGAI.MSG.userBypass(${ctx})`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (window as any).RAGBOT?.send(`RAGAI.MSG.userBypass(${cssMsg})`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (window as any).RAGBOT?.openChatbot();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (window as any).RAGBOT?.blockBackground();
    this.isBotlyLoading = false;
  }

  private applyBotlyCss(css: string): void {
    if (!this.editorView) return;
    const newState = EditorState.create({ doc: css, extensions: this.extensions });
    this.editorView.setState(newState);
    this.finish.emit(css);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    (window as any).RAGBOT?.closeChatbot();
  }
}
